import { ApiError } from '@commissionsight/sdk';
import { type KeyboardEvent, useEffect, useRef, useState } from 'react';
import { useAuth } from '../api.js';
import {
  type AssistantAnswer,
  type ProposedIngest,
  type StatementPreview,
  askAssistant,
  getConversation,
  ingestAttachment,
  investigate,
  previewStatement,
} from './assistant.js';
import { type SavedReport, runSavedReport } from './reports.js';

export interface Turn {
  id: number;
  question: string;
  state: 'thinking' | 'done' | 'error';
  answer?: AssistantAnswer;
  error?: string;
  /** Live progress while thinking: the current stage + steps already taken. */
  statusLabel?: string;
  liveSteps?: { tool: string; summary: string; ok: boolean }[];
  /** Present for statement-attach turns: the preview + the File to ingest. */
  preview?: StatementPreview;
  previewFile?: File;
}

/**
 * The assistant conversation: the turn list, the composer input, the busy
 * flag, and every handler that mutates a turn (ask / investigate / attach /
 * ingest). Also owns conversation continuity — the persisted conversation id
 * (localStorage) and its restore-on-mount.
 */
export function useAssistantStream() {
  const { token, baseUrl, aiPaused, client } = useAuth();
  const [turns, setTurns] = useState<Turn[]>([]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  // Continuity: the persisted conversation this session appends to.
  const [conversationId, setConversationId] = useState<string | null>(() =>
    localStorage.getItem('cs-conversation'),
  );
  const nextId = useRef(1);

  const adoptConversation = (answer: AssistantAnswer) => {
    if (answer.conversationId && answer.conversationId !== conversationId) {
      setConversationId(answer.conversationId);
      localStorage.setItem('cs-conversation', answer.conversationId);
    }
  };

  const newChat = () => {
    setTurns([]);
    setConversationId(null);
    localStorage.removeItem('cs-conversation');
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: restore the persisted conversation once on mount
  useEffect(() => {
    // Restore the persisted conversation (text only — grids recompute on re-ask).
    const stored = localStorage.getItem('cs-conversation');
    if (stored) {
      getConversation(baseUrl, token, stored)
        .then((r) => {
          if (r.data.length === 0) return;
          setTurns(
            r.data.map((t) => ({
              id: nextId.current++,
              question: t.question,
              state: 'done' as const,
              answer: {
                question: t.question,
                intent: (t.intent ?? undefined) as AssistantAnswer['intent'],
                answer: t.answer,
                sql: t.sql ?? undefined,
                reportPath: t.reportPath ?? undefined,
                columns: [],
                rows: [],
                truncated: false,
                followUps: [],
              },
            })),
          );
        })
        .catch(() => {
          localStorage.removeItem('cs-conversation');
          setConversationId(null);
        });
    }
  }, []);

  // Re-run a saved report and show its result as a new turn.
  const runSaved = async (report: SavedReport) => {
    if (busy) return;
    const id = nextId.current++;
    setBusy(true);
    setTurns((t) => [...t, { id, question: `Report: ${report.name}`, state: 'thinking' }]);
    try {
      const res = await runSavedReport(baseUrl, token, report.id);
      const answer: AssistantAnswer = {
        question: report.name,
        intent: 'book_query',
        answer: `Ran “${res.name}” — ${res.rows.length} row${res.rows.length === 1 ? '' : 's'}.`,
        columns: res.columns,
        rows: res.rows,
        truncated: res.truncated,
      };
      setTurns((t) => t.map((x) => (x.id === id ? { ...x, state: 'done', answer } : x)));
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Could not run that report.';
      setTurns((t) => t.map((x) => (x.id === id ? { ...x, state: 'error', error: msg } : x)));
    } finally {
      setBusy(false);
    }
  };

  const send = async (text: string) => {
    const question = text.trim();
    if (!question || busy || aiPaused) return;
    const id = nextId.current++;
    setInput('');
    setBusy(true);
    // Thread completed turns so follow-ups ("…broken down by carrier") resolve.
    const history = turns
      .filter((t) => t.state === 'done' && t.answer)
      .slice(-6)
      .map((t) => ({ question: t.question, sql: t.answer?.sql }));
    // Statements attached in this chat (staged server-side) so "ingest this as
    // June WPS" can resolve which file "this" is.
    const attachmentIds = turns
      .filter((t) => t.state === 'done' && t.preview?.attachmentId)
      .map((t) => t.preview?.attachmentId as string)
      .slice(-5);
    setTurns((t) => [...t, { id, question, state: 'thinking' }]);
    try {
      const answer = await askAssistant(
        baseUrl,
        token,
        question,
        history,
        {
          onStatus: (label) =>
            setTurns((t) => t.map((x) => (x.id === id ? { ...x, statusLabel: label } : x))),
        },
        conversationId,
        attachmentIds,
      );
      adoptConversation(answer);
      setTurns((t) => t.map((x) => (x.id === id ? { ...x, state: 'done', answer } : x)));
    } catch (e) {
      const msg =
        e instanceof ApiError && e.status === 503
          ? 'The AI assistant is not enabled for this environment yet.'
          : e instanceof Error
            ? e.message
            : 'Something went wrong.';
      setTurns((t) => t.map((x) => (x.id === id ? { ...x, state: 'error', error: msg } : x)));
    } finally {
      setBusy(false);
    }
  };

  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send(input);
    }
  };

  // Deep dive: multi-tool investigation of the same question (explicit, slower).
  const onInvestigate = async (question: string) => {
    if (busy || aiPaused) return;
    const id = nextId.current++;
    setBusy(true);
    setTurns((t) => [...t, { id, question: `🔍 Investigate: ${question}`, state: 'thinking' }]);
    try {
      const answer = await investigate(
        baseUrl,
        token,
        question,
        {
          onStatus: (label) =>
            setTurns((t) => t.map((x) => (x.id === id ? { ...x, statusLabel: label } : x))),
          onStep: (step) =>
            setTurns((t) =>
              t.map((x) => (x.id === id ? { ...x, liveSteps: [...(x.liveSteps ?? []), step] } : x)),
            ),
        },
        conversationId,
      );
      adoptConversation(answer);
      setTurns((t) => t.map((x) => (x.id === id ? { ...x, state: 'done', answer } : x)));
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Investigation failed.';
      setTurns((t) => t.map((x) => (x.id === id ? { ...x, state: 'error', error: msg } : x)));
    } finally {
      setBusy(false);
    }
  };

  // Statement attach: preview the file in the conversation (nothing ingested yet).
  const attach = async (file: File) => {
    if (busy) return;
    const id = nextId.current++;
    setBusy(true);
    setTurns((t) => [...t, { id, question: `📎 ${file.name}`, state: 'thinking' }]);
    try {
      const preview = await previewStatement(baseUrl, token, file);
      setTurns((t) =>
        t.map((x) => (x.id === id ? { ...x, state: 'done', preview, previewFile: file } : x)),
      );
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Could not read that file.';
      setTurns((t) => t.map((x) => (x.id === id ? { ...x, state: 'error', error: msg } : x)));
    } finally {
      setBusy(false);
    }
  };

  // Carrier switched in the preview card → re-preview against THAT carrier so the
  // shape check and the preflight questions apply to what will actually ingest.
  const repreview = async (turnId: number, file: File, carrierId: string) => {
    const preview = await previewStatement(baseUrl, token, file, carrierId);
    setTurns((t) => t.map((x) => (x.id === turnId ? { ...x, preview } : x)));
  };

  // Approve a conversational-ingest proposal ("ingest this as June WPS" → Approve).
  const approveIngest = async (turnId: number, p: ProposedIngest): Promise<string> => {
    const res = await ingestAttachment(baseUrl, token, {
      attachmentId: p.attachmentId,
      carrierId: p.carrierId,
      periodYear: p.periodYear,
      periodMonth: p.periodMonth,
      replace: p.replace,
    });
    setTurns((t) =>
      t.map((x) =>
        x.id === turnId && x.answer
          ? {
              ...x,
              answer: {
                ...x.answer,
                proposedIngest: undefined,
                answer: `${p.fileName} is uploaded and queued for processing (job ${res.jobId}). You'll get the statement brief here and by email when it finishes.`,
              },
            }
          : x,
      ),
    );
    return res.jobId;
  };

  // Confirmed ingest — the NORMAL upload path (same endpoint as the Files page).
  const ingestPreviewed = async (
    turnId: number,
    file: File,
    carrierId: string,
    period: string,
  ): Promise<string> => {
    const [y, m] = period.split('-').map(Number);
    if (!client || !y || !m) throw new Error('Pick a statement month first.');
    const res = await client.uploadFile({
      file,
      carrierId,
      periodYear: y,
      periodMonth: m,
    });
    setTurns((t) =>
      t.map((x) =>
        x.id === turnId
          ? {
              ...x,
              preview: undefined,
              previewFile: undefined,
              answer: {
                question: x.question,
                intent: 'book_query',
                answer: `Uploaded and queued for processing (job ${res.jobId}). You'll get the statement brief here and by email when it finishes.`,
              },
            }
          : x,
      ),
    );
    return res.jobId;
  };

  return {
    turns,
    input,
    setInput,
    busy,
    newChat,
    runSaved,
    send,
    onKeyDown,
    onInvestigate,
    attach,
    repreview,
    approveIngest,
    ingestPreviewed,
  };
}
